import React, { ChangeEvent } from 'react';
import { UcLoading } from '../common/UcLoading';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend,  ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartInstanceOfMuiTheme, UomAxisLabel } from 'components/readings/CustomerSummaryChartUtils';
// @ts-ignore
import { FormControlLabel, FormGroup, Grid, Switch, Typography, useTheme } from '@mui/material';
// @ts-ignore
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { useResponsive } from '../../lib/useResponsive';
// @ts-ignore
import Alert from '@mui/material/Alert';
import useOrgAppSettings from 'lib/useOrgAppSettings';
import { SwrOptions } from "../../lib/useGenericSWR.interfaces";
import { updateHistoricalUsageWithWeather, HistoricalComparisonChartProps } from './HistoricalComparisonChartUtils';
import { useMyOrgs } from '../customer/orgs/MyOrgAPIEndpoints';
// @ts-ignore
import { getPrecipUnit } from '../../lib/Formatters';
import { green, indigo } from "@mui/material/colors";
// @ts-ignore
import { useUserPref } from "components/user/useUserPref";
import { HistoricalComparisonCustomTooltip } from './HistoricalComparisonCustomTooltip';
// @ts-ignore
import { DateTime } from 'luxon';
import { useMyTimezone } from 'components/customer/orgs/MyOrgAPIEndpoints';
import { useHistoricalComparison, useHistoricalComparisonDailyWeather } from './HistoricalComparisonApi';
import { useMyWeatherChartData } from './MyWeatherChartApi';

export const HistoricalComparisonChart = (props: HistoricalComparisonChartProps) => {
    const { chartType, yearOne, yearTwo, setCsvDataToDownload } = props;
    const theme = useTheme<ChartInstanceOfMuiTheme>();
    const { t } = useTranslation();
    const timezone = useMyTimezone();
    const { isMobile } = useResponsive();
    const { orgAppSettings } = useOrgAppSettings();
    const showPrecipHistorical = useUserPref("showPrecipHistorical", true);
    
    // Monthly weather data
    const swrWeatherHistorySummary = useMyWeatherChartData(props.startDate, props.endDate, props.chartType, props.geoCoordinates);

    // Daily weather data with both years combined
    const swrWeatherHistoryDailySummary = useHistoricalComparisonDailyWeather(
        props.chartType === "day" ? props.startDate : null,
        props.chartType === "day" ? props.endDate : null,
        props.yearOne,
        props.yearTwo,
        props.geoCoordinates
    );

    const uom: string = props.uom || "KWH";
    const selectedMeter: string = props.selectedMeter || "All";
    let orgTempUnit: string | null = null;

    const swrHistoricalUsage = useHistoricalComparison(props.chartType, props.accountId, selectedMeter, uom, props.startDate, props.endDate, props.yearOne, props.yearTwo, {} as SwrOptions);
    const swrMyOrg = useMyOrgs();

    if (!swrMyOrg.isLoading && swrMyOrg.result?.length > 0) {
        orgTempUnit = swrMyOrg.result[0].temperatureUnit;
    }

    React.useEffect(() => {
        if (!swrHistoricalUsage.isLoading && !swrHistoricalUsage.isError) {
            props.setDateRangeStart(DateTime.fromISO(swrHistoricalUsage?.result?.minReadingDate, { zone: timezone }).toJSDate());
            props.setDateRangeEnd(DateTime.fromISO(swrHistoricalUsage?.result?.maxReadingDate, { zone: timezone }).toJSDate());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [swrHistoricalUsage?.result?.minReadingDate, swrHistoricalUsage?.result?.maxReadingDate, swrHistoricalUsage.isLoading, swrHistoricalUsage.isError, timezone])

    const [data, setData] = React.useState<any>(null);
    const hasProcessedRef = React.useRef(false);

    // Reset the ref when dependencies change
    React.useEffect(() => {
        hasProcessedRef.current = false;
    }, [chartType, yearOne, yearTwo, swrHistoricalUsage?.result?.results, swrWeatherHistoryDailySummary?.result?.days, swrWeatherHistorySummary?.result]);

    // Process data when dependencies change
    React.useEffect(() => {
        if (!swrHistoricalUsage.isLoading && !swrHistoricalUsage.isError) {
            let chartData = swrHistoricalUsage?.result?.results;
            let newData = null;

            if (chartType === "day" && swrWeatherHistoryDailySummary?.result?.days?.length > 0) {
                newData = updateHistoricalUsageWithWeather(swrWeatherHistoryDailySummary, swrHistoricalUsage, yearOne, yearTwo, chartType);
            }
            else if (chartType === "month") {
                if (swrWeatherHistorySummary?.result?.length > 0) {
                    chartData = updateHistoricalUsageWithWeather(swrWeatherHistorySummary, swrHistoricalUsage, yearOne, yearTwo, chartType);
                }

                newData = chartData?.map((item: any) => ({
                    ...item,
                    startDate: new Date(item.startDate).toLocaleString('default', {
                        month: 'long',
                        day: 'numeric'
                    }),
                    endDate: new Date(item.endDate).toLocaleString('default', {
                        month: 'long',
                        day: 'numeric'
                    }),
                }));
            }
            else {
                newData = chartData;
            }

            if (!hasProcessedRef.current) {
                setData(newData);
                if (newData) {
                    setCsvDataToDownload(newData);
                    hasProcessedRef.current = true;
                }
            }
        }
    }, [chartType, yearOne, yearTwo, setCsvDataToDownload, swrHistoricalUsage, swrWeatherHistoryDailySummary, swrWeatherHistorySummary]);

    const handleChangeShowPrecip = (event: ChangeEvent<HTMLInputElement>) => {
        showPrecipHistorical.setUserPref("showPrecipHistorical", !(showPrecipHistorical.valueBool));
    }

    const uomAxisLabel: { [key: string]: UomAxisLabel } = {
        'KWH': t("Electricity ({{uom}})", { uom: orgAppSettings?.result?.uomMappings['KWH']?.safeUomDisplay }),
        'GAL': t("Water ({{uom}})", { uom: orgAppSettings?.result?.uomMappings['GAL']?.safeUomDisplay }),
    }

    const formatXAxis = (tick: any) => {
        if (props.chartType === "month"){
            return tick.split(' ')[0];
        }
        if (props.chartType === "day") {
            return new Date(tick).toLocaleString('default', {
                month: 'long',
                day: 'numeric'
            });
        }
    };

    return (
        <div className="historical-usage-comparison-chart" data-cy-id="myHistoricalComparisonGraph" style={{ width: "100%", height: "670px" }}>
            <UcLoading hasError={swrHistoricalUsage.hasError} isLoading={swrHistoricalUsage.isLoading} hasFade={false}>
                {data?.length > 0 ?
                    <>
                        {isMobile && (
                            <Grid container>
                                <Grid item xs={6} justifyContent="left" paddingLeft="5px">
                                    <Typography fontSize="16px" color={theme?.props.Rechart.axisColor}> {orgAppSettings?.result?.uomMappings[uom]?.safeUomDisplay} </Typography>
                                </Grid>
                            </Grid>
                        )}
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                                width={500}
                                data={data}
                                margin={{                                  
                                    left: isMobile ? -10 : 20,
                                    bottom: 5,
                                    right: 20,
                                    top: 20
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="startDate"
                                    tickLine={false}
                                    tickFormatter={(tick) => formatXAxis(tick)}
                                    tick={props.chartType === "day" ? { fontSize: 12 } : { fontSize: "auto" }}
                                />
                                <YAxis
                                    label={{ value: isMobile ? "" : uomAxisLabel[uom], angle: -90, position: "left" }}
                                    axisLine={!isMobile}
                                />
                                {/* @ts-ignore */}
                                <Tooltip trigger='click' content={HistoricalComparisonCustomTooltip} uom={orgAppSettings?.result?.uomMappings[uom]?.safeUomDisplay} orgTempUnit={orgTempUnit} chartType={props.chartType} />
                                <Legend wrapperStyle={{ paddingLeft: isMobile ? "20px" : "0" }} />
                                <Bar dataKey="totalUsageYearOne" name={props.yearOne} fill={indigo[300]}>
                                    {props.chartType === "month" && (
                                        <LabelList
                                            dataKey="weatherDataYearOne"
                                            position="top"
                                            formatter={(value: any) => `${value}${String.fromCharCode(176)}${orgTempUnit}`}
                                        />
                                    )}                                  
                                </Bar>
                                <Bar dataKey="totalUsageYearTwo" name={props.yearTwo} fill={green[300]}>
                                    {props.chartType === "month" && (
                                        <LabelList
                                            dataKey="weatherDataYearTwo"
                                            position="top"
                                            formatter={(value: any) => `${value}${String.fromCharCode(176)}${orgTempUnit}`}
                                        />
                                    )}                                 
                                </Bar>
                                <YAxis
                                    yAxisId="precip"
                                    orientation="right"
                                    label={{ value: isMobile ? "" : t("Precipitation (" + getPrecipUnit(orgTempUnit) + ")"), angle: -90, position: "insideRight", offset: 20 }}
                                    hide={isMobile ?? !showPrecipHistorical.valueBool}
                                />
                                <Line
                                    yAxisId="precip"
                                    hide={!showPrecipHistorical.valueBool}
                                    dataKey="precipDataYearOne"
                                    name={"Precip. " + props.yearOne}
                                    activeDot={{ r: 8 }}
                                    isAnimationActive={false}
                                    type="monotone"
                                    fill={indigo[600]}
                                    stroke={indigo[600]}
                                    legendType={"line"}
                                />
                                <Line
                                    yAxisId="precip"
                                    hide={!showPrecipHistorical.valueBool}
                                    dataKey="precipDataYearTwo"
                                    name={"Precip. " + props.yearTwo}
                                    activeDot={{ r: 8 }}
                                    isAnimationActive={false}
                                    type="monotone"
                                    fill={green[600]}
                                    stroke={green[600]}
                                    legendType={"line"}
                                />
                                {props.chartType === "day" && (
                                    <>
                                        <YAxis
                                            yAxisId="temp"
                                            orientation="right"
                                            label={{ value: isMobile ? "" : t("Temperature (\u00B0" + orgTempUnit + ")"), angle: -90, position: "insideRight", offset: 0 }}
                                            hide={isMobile}
                                            tickFormatter={(value) => {
                                                return value + "\u00B0";
                                            }}
                                        />
                                        <Line
                                            yAxisId="temp"
                                            hide={isMobile}
                                            dataKey="weatherDataYearOne"
                                            name={"Temp. " + props.yearOne}
                                            strokeDasharray="5 5"
                                            activeDot={{ r: 8 }}
                                            isAnimationActive={false}
                                            type="monotone"
                                            fill={indigo[300]}
                                            stroke={indigo[300]}
                                            legendType={"line"}
                                        />
                                        <Line
                                            yAxisId="temp"
                                            hide={isMobile}
                                            dataKey="weatherDataYearTwo"
                                            name={"Temp. " + props.yearTwo}
                                            strokeDasharray="5 5"
                                            activeDot={{ r: 8 }}
                                            isAnimationActive={false}
                                            type="monotone"
                                            fill={green[300]}
                                            stroke={green[300]}
                                            legendType={"line"}
                                        />
                                    </>                                
                                )}
                                
                            </ComposedChart>
                        </ResponsiveContainer>
                        <FormGroup className="form-inline" style={{ flexDirection: 'row', paddingRight: "15px", justifyContent: 'flex-end' }}>
                            <FormControlLabel
                                control={<Switch checked={(showPrecipHistorical.valueBool)}
                                    onChange={handleChangeShowPrecip}
                                    inputProps={{ 'aria-label': 'controlled' }} />
                                }
                                labelPlacement="start"
                                label={t(`Precipitation (${getPrecipUnit(orgTempUnit)})`)}
                            />
                        </FormGroup>  
                    </>                 
                    :
                    <Alert severity="warning">{t("No historical usage data available for comparison.")}</Alert>
                }
            </UcLoading>
        </div>
    );
};